// contexts/AuthContext.js
import { createContext, useContext, useEffect, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import authService from '../services/authService';
import performanceMonitor from '../utils/performanceMonitor';
import apiClient from '../services/apiClient'; // Ajout de l'import manquant
import API_ENDPOINTS from '../config/apiEndpoints'; // Ajout de l'import manquant
import { clearAuth, setCredentials } from '../store/slices/authSlice';

export const AuthContext = createContext(null);

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload?.user, // Stocker l'objet utilisateur
                loading: false,
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                loading: false,
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload,
            };
        case 'SET_USER': // Action pour mettre à jour l'utilisateur si nécessaire
            return {
                ...state,
                user: action.payload,
            };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const reduxDispatch = useDispatch();
    const [state, dispatch] = useReducer(authReducer, {
        isAuthenticated: false,
        user: null,
        loading: true,
    });

    useEffect(() => {
        const checkUserSession = async () => {
            try {
                // L'apiClient enverra automatiquement le cookie de session s'il existe.
                const response = await apiClient.get(API_ENDPOINTS.AUTH.USER);
                if (response.data) {
                    dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data } });
                    reduxDispatch(setCredentials({
                        user: response.data,
                        access: authService.getAccessToken(),
                        refresh: authService.getRefreshToken(),
                    }));
                }
            } catch (error) {
                // Une erreur (ex: 401) signifie simplement qu'il n'y a pas de session active.
                console.log('AuthContext: No active session found on initial load.');
                dispatch({ type: 'LOGOUT' });
                reduxDispatch(clearAuth());
            } finally {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        checkUserSession();
    }, [reduxDispatch]); // Le tableau de dépendances vide assure une exécution unique au montage.

    const login = async (email, password) => {
        console.log("AuthContext login: Attempting login...");
        performanceMonitor.markLoginStart(); // Démarrer le tracking de performance
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const loginResponse = await authService.login(email, password);
            console.log("AuthContext login: authService.login successful. Response:", loginResponse);

            if (loginResponse && loginResponse.success && loginResponse.user) {
                dispatch({ type: 'LOGIN_SUCCESS', payload: { user: loginResponse.user } });
                reduxDispatch(setCredentials({
                    user: loginResponse.user,
                    access: authService.getAccessToken(),
                    refresh: authService.getRefreshToken(),
                }));
                performanceMonitor.trackLogin(loginResponse.user?.id || 'unknown_user');
                return loginResponse;
            } else {
                console.error("AuthContext login: Login response missing success or user data.", loginResponse);
                dispatch({ type: 'LOGOUT' });
                throw new Error(loginResponse?.message || "Erreur de connexion: Données de session invalides.");
            }
        } catch (error) {
            console.error('AuthContext login: Error during login process:', error.message);
            dispatch({ type: 'LOGOUT' });
            performanceMonitor.trackLoginFailure(email, error.message);
            throw error;
        }
    };

    const logout = async () => {
        console.log("AuthContext logout: Initiating logout.");
        dispatch({ type: 'SET_LOADING', payload: true });
        await authService.logout(); // Appelle le backend pour invalider les cookies
        dispatch({ type: 'LOGOUT' });
        reduxDispatch(clearAuth());
        performanceMonitor.trackLogout();
        console.log("AuthContext logout: Logout process completed.");
    };

    return (
        <AuthContext.Provider value={{ ...state, login, logout, isAuthenticated: state.isAuthenticated, user: state.user, loading: state.loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
